import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const Scale = ({ value, onChange, testId }) => (
  <div className="flex gap-2" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testId}-${n}`}
        className={`w-11 h-11 border text-sm font-medium transition-colors ${value === n ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
        {n}
      </button>
    ))}
  </div>
);

export default function AvgsFeedbackPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/avgs-feedback/respond/${token}`).then(({ data }) => setInfo(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [token]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/avgs-feedback/respond/${token}`, { answers, comment });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 p-8 text-center" data-testid="avgs-feedback-error">
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-2">Feedbackbogen nicht verfügbar</div>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen flex items-center justify-center text-xs text-slate-400">Lädt…</div>;

  const count = Object.keys(answers).length;
  const thanks = info.already_submitted || done;
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl bg-white border border-slate-200" data-testid="avgs-feedback-page">
        <div className="px-6 sm:px-8 py-6 border-b border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">Feedbackbogen Coaching</div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">
            {thanks ? "Vielen Dank!" : `Hallo${info.first_name ? " " + info.first_name : ""}.`}
          </div>
          {!thanks && <p className="text-xs text-slate-500 mt-2 leading-relaxed">Bewertungsskala: <b>1 = trifft nicht zu</b> · <b>5 = trifft voll zu</b></p>}
        </div>
        <div className="px-6 sm:px-8 py-6">
          {thanks ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center flex flex-col items-center gap-2" data-testid="avgs-feedback-done">
              <CheckCircle2 size={32} strokeWidth={1.5} className="text-emerald-600" />
              <div className="font-medium text-emerald-800">Ihre Rückmeldung wurde gespeichert.</div>
              <div className="text-xs text-emerald-700">Wir wünschen Ihnen viel Erfolg auf Ihrem weiteren Weg!</div>
            </div>
          ) : (
            <div className="space-y-7">
              {info.questions.map((q, i) => (
                <div key={q.id} data-testid={`avgs-feedback-question-${q.id}`}>
                  <div className="text-sm text-slate-800 mb-2 leading-snug">{i + 1}. {q.text}</div>
                  <Scale value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} testId={`avgs-feedback-rating-${q.id}`} />
                </div>
              ))}
              <div>
                <div className="text-sm text-slate-800 mb-2">Optional: Was war für Sie besonders hilfreich? Was können wir verbessern?</div>
                <textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="input-tech resize-none" data-testid="avgs-feedback-comment" />
              </div>
              <div className="text-xs text-slate-500" data-testid="avgs-feedback-progress">{count} von {info.questions.length} Fragen beantwortet</div>
              <button onClick={submit} disabled={saving || count < info.questions.length} data-testid="avgs-feedback-submit"
                className="w-full bg-slate-900 text-white py-4 text-sm font-medium hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {saving ? "Sendet…" : "Absenden"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
